<template>
  <div class="ai-generate-knowledge">
    <div class="history-toolbar">
      <el-form :inline="true" :model="filters" size="small" class="toolbar-form" @submit.native.prevent="fetchHistory">
        <el-form-item label="结果标题">
          <el-input 
            v-model="filters.resultTitle" 
            clearable 
            placeholder="内容关键字..." 
            prefix-icon="el-icon-search"
            class="search-input-title"
          ></el-input>
        </el-form-item>
        <el-form-item label="模板">
          <el-select v-model="filters.templateCode" clearable placeholder="全部模板" class="search-select">
            <el-option v-for="t in templates" :key="t.code" :label="t.name" :value="t.code"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filters.status" clearable placeholder="全部状态" class="search-select-status">
            <el-option label="待处理" value="PENDING"></el-option>
            <el-option label="进行中" value="PROCESSING"></el-option>
            <el-option label="已完成" value="COMPLETED"></el-option>
            <el-option label="已失败" value="FAILED"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item class="toolbar-btns">
          <el-button type="primary" icon="el-icon-search" @click="fetchHistory">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
      <el-button type="primary" size="small" icon="el-icon-plus" @click="goCreate">
        创建任务
      </el-button>
    </div>

    <el-table
      :data="historyList"
      v-loading="loading"
      class="history-table"
      height="100%"
    >
      <el-table-column prop="templateName" label="模板" width="160">
        <template slot-scope="scope">
          {{ scope.row.templateName || scope.row.name || scope.row.templateCode || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="title" label="生成标题" min-width="250">
        <template slot-scope="scope">
          {{ scope.row.title || scope.row.resultTitle || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="120">
        <template slot-scope="scope">
          <el-tag :type="getStatusTagType(scope.row.status)" size="mini">
            {{ getStatusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="detailCount" label="数据源条数" width="110">
        <template slot-scope="scope">
          {{ scope.row.detailCount || scope.row.mergeCount || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="createdBy" label="创建人" width="120">
        <template slot-scope="scope">
          {{ userMap[scope.row.createdBy] || scope.row.createdBy || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="createdTime" label="创建时间" width="170">
        <template slot-scope="scope">
          {{ formatTime(scope.row.createdTime || scope.row.createdAt || scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="completedTime" label="完成时间" width="170">
        <template slot-scope="scope">
          {{ formatTime(scope.row.completedTime || scope.row.finishedTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="120">
        <template slot-scope="scope">
          <el-button
            type="text"
            size="mini"
            v-if="!isCompleted(scope.row)"
            @click="openEdit(scope.row)"
          >
            编辑
          </el-button>
          <el-button
            type="text"
            size="mini"
            v-else
            @click="openView(scope.row)"
          >
            查看
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-section">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :current-page="pagination.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>

    <el-dialog
      title="合并结果"
      :visible.sync="resultDialogVisible"
      width="720px"
      @close="resetResultDialog"
    >
      <div v-loading="resultLoading" class="result-dialog-body">
        <template v-if="resultError">
          <el-alert type="error" :closable="false" :title="resultError"></el-alert>
        </template>
        <template v-else-if="resultDetail">
          <div class="result-title">{{ resultDetail.title || '未命名内容' }}</div>
          <div class="result-meta">
            <span>状态：{{ getStatusText(resultDetail.status) }}</span>
            <span>条数：{{ resultDetail.detailCount || '-' }}</span>
            <span>Token：{{ resultDetail.totalTokens || '-' }}</span>
          </div>
          <div class="result-content" v-html="resultMarkdownHtml"></div>
        </template>
        <template v-else>
          <div class="empty-tip">暂无结果数据</div>
        </template>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="resultDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MarkdownIt from "markdown-it";
import { requestUrl } from "@/http/requestUrl";
import { normalizeQuota, isQuotaExceeded } from "@/utils/quota";

export default {
  name: "aiGenerateKnowledge",
  data() {
    return {
      loading: false,
      historyList: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      filters: {
        resultTitle: "",
        templateCode: "",
        status: ""
      },
      templates: [], // 模板列表
      userMap: {}, // 用户ID到姓名的映射缓存
      resultDialogVisible: false,
      resultLoading: false,
      resultDetail: null,
      resultError: "",
      resultMarkdownHtml: "",
      markdownRenderer: null,
      tokenQuota: {
        checked: false,
        exceeded: false,
        used: null,
        total: null
      },
      tokenQuotaChecking: false
    };
  },
  mounted() {
    this.markdownRenderer = new MarkdownIt({
      html: true,
      linkify: true
    });
    this.fetchHistory();
    this.fetchTemplates();
    this.fetchTokenQuota();
  },
  methods: {
    async goCreate() {
      const canCreate = await this.ensureTokenQuota();
      if (!canCreate) return;
      this.$router.push("/ai_generate_knowledge/create");
    },
    async fetchTokenQuota() {
      if (this.tokenQuotaChecking) return this.tokenQuota;
      this.tokenQuotaChecking = true;
      try {
        const res = await this.$http.get(requestUrl.company.getUserCompanyModule);
        const payload = (res.data && res.data.data) ? res.data.data : (res.data || {});
        const companyConfig = payload.companyConfig || {};
        let quota = normalizeQuota(payload, {
          usedKeys: ["tokenUsed", "usedTokens", "flowUsed", "usedFlow", "useFlow", "usedToken", "llmTokenUsed"],
          totalKeys: ["tokenTotal", "totalTokens", "flowTotal", "totalFlow", "maxTokens", "maxFlow", "llmTokenLimit"],
          remainingKeys: ["canUsedFlow", "flowSurplus", "tokenSurplus", "remainingTokens"]
        });
        if (typeof companyConfig.llmTokenLimit === "number") {
          quota = {
            ...quota,
            total: companyConfig.llmTokenLimit
          };
        }
        const usedRes = await this.$http.post("/portal-api/token-usage/logs/page", {
          pageNo: 1,
          pageSize: 1
        });
        const usedPayload = (usedRes.data && usedRes.data.data) ? usedRes.data.data : usedRes.data;
        let usedValue = usedPayload && typeof usedPayload === "object"
          ? (typeof usedPayload.totalTokens === "number"
            ? usedPayload.totalTokens
            : (Number(usedPayload.promptTokens || 0) + Number(usedPayload.completionTokens || 0)))
          : null;
        if (usedValue === null && usedRes.data && usedRes.data.code === "5001") {
          usedValue = 0;
        }
        if (typeof usedValue === "number" && !Number.isNaN(usedValue)) {
          quota = {
            ...quota,
            used: usedValue
          };
        }
        this.tokenQuota = {
          checked: true,
          exceeded: quota.total === 0 || quota.total === null ? true : isQuotaExceeded(quota),
          used: quota.used,
          total: quota.total
        };
      } catch (error) {
        console.error("fetchTokenQuota error:", error);
        this.tokenQuota.checked = true;
      } finally {
        this.tokenQuotaChecking = false;
      }
      return this.tokenQuota;
    },
    async ensureTokenQuota() {
      const quota = await this.fetchTokenQuota();
      if (quota.exceeded) {
        this.$message.warning("Token已经使用完，请联系商务购买。");
        return false;
      }
      return true;
    },
    fetchHistory() {
      if (this.loading) return;
      this.loading = true;
      const params = {
        page: this.pagination.page,
        pageSize: this.pagination.pageSize
      };
      if (this.filters.resultTitle) {
        params.resultTitle = this.filters.resultTitle.trim();
      }
      if (this.filters.templateCode) {
        params.templateCode = this.filters.templateCode;
      }
      if (this.filters.status) {
        params.status = this.filters.status;
      }
      this.$http
        .get(requestUrl.collection.taskResultMergeHistory, { params })
        .then(res => {
          if (res.data && res.data.code === "0") {
            const pageData = res.data.data || {};
            const list = Array.isArray(pageData.list)
              ? pageData.list
              : Array.isArray(pageData.records)
                ? pageData.records
                : [];
            this.historyList = list;
            this.pagination.total = pageData.total || 0;
            if (list.length > 0) {
              this.fetchUserNames(list);
            }
          } else {
            this.$message.error(res.data && res.data.message ? res.data.message : "获取历史记录失败");
          }
        })
        .catch(error => {
          console.error("fetchHistory error:", error);
          this.$message.error("获取历史记录失败，请稍后重试");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handlePageChange(page) {
      this.pagination.page = page;
      this.fetchHistory();
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.fetchHistory();
    },
    resetFilters() {
      this.filters = {
        resultTitle: "",
        templateCode: "",
        status: ""
      };
      this.pagination.page = 1;
      this.fetchHistory();
    },
    fetchTemplates() {
      this.$http
        .get(requestUrl.collection.taskResultMergeTemplates)
        .then(res => {
          if (res.data && res.data.code === "0") {
            this.templates = Array.isArray(res.data.data) ? res.data.data : [];
          }
        })
        .catch(error => {
          console.error("fetchTemplates error:", error);
        });
    },
    formatTime(time) {
      if (!time) return "-";
      const date = new Date(time);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    getRecordId(row) {
      if (!row || typeof row !== "object") return "";
      return row.recordId || row.id || row.mergeRecordId || "";
    },
    getStatusText(status) {
      const map = {
        PENDING: "待处理",
        PROCESSING: "进行中",
        COMPLETED: "已完成",
        FAILED: "已失败"
      };
      return map[status] || "未知";
    },
    getStatusTagType(status) {
      const map = {
        PENDING: "info",
        PROCESSING: "warning",
        COMPLETED: "success",
        FAILED: "danger"
      };
      return map[status] || "info";
    },
    canViewResult(row) {
      return row && (row.status === "COMPLETED" || row.status === "FAILED");
    },
    isCompleted(row) {
      return row && row.status === "COMPLETED";
    },
    cacheRecord(row) {
      const recordId = this.getRecordId(row);
      if (!recordId) return;
      const key = "ai_merge_record_cache";
      const cache = JSON.parse(sessionStorage.getItem(key) || "{}");
      cache[recordId] = row;
      sessionStorage.setItem(key, JSON.stringify(cache));
    },
    openEdit(row) {
      const recordId = this.getRecordId(row);
      if (!recordId) {
        this.$message.warning("未获取到记录ID");
        return;
      }
      this.cacheRecord(row);
      this.$router.push({
        path: "/ai_generate_knowledge/create",
        query: { recordId, mode: "edit" }
      });
    },
    openView(row) {
      const recordId = this.getRecordId(row);
      if (!recordId) {
        this.$message.warning("未获取到记录ID");
        return;
      }
      this.cacheRecord(row);
      this.$router.push({
        path: "/ai_generate_knowledge/create",
        query: { recordId, mode: "view" }
      });
    },
    openResultDialog(row) {
      const recordId = this.getRecordId(row);
      if (!recordId) {
        this.$message.warning("未获取到记录ID");
        return;
      }
      this.resultDialogVisible = true;
      this.resultLoading = true;
      this.resultDetail = null;
      this.resultError = "";
      this.resultMarkdownHtml = "";
      this.$http
        .get(`${requestUrl.collection.taskResultMergeResult}/${recordId}`)
        .then(res => {
          if (res.data && res.data.code === "0") {
            const detail = res.data.data || {};
            this.resultDetail = detail;
            if (detail.status === "FAILED" && detail.errorMessage) {
              this.resultError = detail.errorMessage;
            }
            this.resultMarkdownHtml = detail.content
              ? this.markdownRenderer.render(
                  detail.content.replace(/<code data-type="yaml-front-matter" class="language-yaml">[\s\S]*?<\/code>/g, "")
                )
              : "";
          } else {
            this.resultError = res.data && res.data.message ? res.data.message : "获取结果失败";
          }
        })
        .catch(error => {
          console.error("openResultDialog error:", error);
          this.resultError = "获取结果失败，请稍后重试";
        })
        .finally(() => {
          this.resultLoading = false;
        });
    },
    fetchUserNames(list) {
      if (!list || list.length === 0) return;
      const newIds = [...new Set(list.map(item => item.createdBy).filter(Boolean))]
        .filter(id => !this.userMap[id]);
      if (newIds.length === 0) return;
      const promises = newIds.map(id => 
        this.$http.get("/portal-api/account/appoint/" + id)
          .then(res => {
            if (res.data && res.data.code === "0" && res.data.data) {
              const user = res.data.data;
              return { id: id, name: user.realName || user.username || id };
            }
            return { id: id, name: id };
          })
          .catch(() => ({ id: id, name: id }))
      );
      Promise.all(promises).then(results => {
        const newUserMap = { ...this.userMap };
        results.forEach(result => {
          if (result && result.id) {
            newUserMap[result.id] = result.name;
          }
        });
        this.userMap = newUserMap;
      });
    },
    resetResultDialog() {
      this.resultDetail = null;
      this.resultError = "";
      this.resultMarkdownHtml = "";
    }
  }
};
</script>

<style scoped>
.ai-generate-knowledge {
  width: 100%;
  height: calc(100vh - 100px); /* Adjust based on your header height */
  background-color: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.history-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.toolbar-form {
  flex: 1;
}

.toolbar-form .el-form-item {
  margin-bottom: 0;
  margin-right: 12px;
}

.search-input-title {
  width: 160px;
}
.search-select {
  width: 130px;
}
.search-select-status {
  width: 100px;
}

.history-table {
  background: #ffffff;
  border: none;
  flex: 1;
  overflow: hidden;
}

::v-deep .history-table.el-table {
  border: none;
}

::v-deep .history-table.el-table::before {
  display: none;
}

::v-deep .history-table .el-table__header-wrapper th {
  background: #fff;
  color: #1f2d3d;
  font-weight: 600;
  height: 48px;
}

::v-deep .history-table .el-table__body td {
  height: 50px;
  color: #2f3a4c;
}

::v-deep .history-table .el-table__body tr:hover > td {
  background: #f5f7ff;
}

.pagination-section {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.result-dialog-body {
  min-height: 180px;
}

.result-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #1f2d3d;
}

.result-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #6b7b8c;
  margin-bottom: 12px;
}

.result-content {
  max-height: 360px;
  overflow-y: auto;
  padding: 12px;
  border: 1px solid #e6eaf2;
  border-radius: 6px;
  background: #f9fbff;
  color: #1f2d3d;
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 30px 0;
}
</style>
